/**
 * CivicPulse chrome — header + footer used across every /civic/* route.
 *
 * Palette:
 *   green primary   #0F7B3F   (leaf/logo, buttons, resolved badges)
 *   green deep      #0B4E29   (nav text)
 *   navy accent     #0B2545   ("Report • Connect • Resolve • Improve")
 *   cream bg        #FAFAF7   (page background)
 *   golden accent   #E9A400   ("Improve" word)
 */
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Leaf, Search, MapPin } from "lucide-react";
import { civicPath } from "@/pages/civic/basePath";
import { useCivicI18n } from "@/pages/civic/useCivicI18n";
import CivicLanguageToggle from "@/components/civic/CivicLanguageToggle";

const NAV_KEYS = [
  { to: "/map",         key: "nav.map",         fallback: "Civic Map" },
  { to: "/report",      key: "nav.report",      fallback: "Report" },
  { to: "/resolved",    key: "nav.resolved",    fallback: "Resolved" },
  { to: "/communities", key: "nav.communities", fallback: "Communities" },
  { to: "/authorities", key: "nav.authorities", fallback: "For Authorities" },
];

export function CivicHeader() {
  const { t } = useCivicI18n();
  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-emerald-100"
      data-testid="civic-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-8">
        <Link to={civicPath("/")} className="flex items-center gap-2" data-testid="civic-wordmark">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-100 ring-1 ring-emerald-200">
            <Leaf className="w-5 h-5 text-emerald-700" strokeWidth={2.2} />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-xl font-bold text-slate-900 tracking-tight">
              CivicPulse<span className="text-emerald-700">.blog</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Powered by Social&nbsp;Pulse<span className="text-[8px] align-top">®</span>
            </div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          {NAV_KEYS.map((n) => (
            <NavLink
              key={n.to}
              to={civicPath(n.to)}
              data-testid={`civic-nav-${n.fallback.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-emerald-700" : "text-slate-700 hover:text-emerald-700"}`
              }
            >
              {t(n.key, n.fallback)}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 text-sm font-semibold">
          <button
            aria-label={t("search.label", "Search")}
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-emerald-50 text-slate-700"
            data-testid="civic-search-btn"
          >
            <Search className="w-4 h-4" />
          </button>
          <CivicLanguageToggle />
          <div className="hidden lg:flex items-center gap-1 tracking-tight text-[13px] ml-2">
            <span className="text-sky-700">{t("tag.report", "Report")}</span>
            <span className="text-slate-400">•</span>
            <span className="text-emerald-700">{t("tag.connect", "Connect")}</span>
            <span className="text-slate-400">•</span>
            <span className="text-amber-600">{t("tag.resolve", "Resolve")}</span>
            <span className="text-slate-400">•</span>
            <span className="text-sky-700">{t("tag.improve", "Improve")}</span>
          </div>
        </div>
      </div>
    </header>
  );
}

export function CivicFooter() {
  return (
    <footer className="mt-16 border-t border-emerald-100 bg-white" data-testid="civic-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 md:grid-cols-4 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="grid place-items-center w-8 h-8 rounded-full bg-emerald-100">
              <Leaf className="w-4 h-4 text-emerald-700" />
            </span>
            <div className="font-serif text-lg font-bold text-slate-900">CivicPulse.blog</div>
          </div>
          <p className="text-slate-600 leading-relaxed">
            Report once, route correctly, display locally, and keep the
            issue visible until the community confirms it is truly resolved.
          </p>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">Explore</div>
          <ul className="space-y-1.5 text-slate-600">
            <li><Link to={civicPath("/map")} className="hover:text-emerald-700">Civic map</Link></li>
            <li><Link to={civicPath("/report")} className="hover:text-emerald-700">Report a problem</Link></li>
            <li><Link to={civicPath("/india/kerala")} className="hover:text-emerald-700">Kerala pilot</Link></li>
            <li><Link to={civicPath("/resolved")} className="hover:text-emerald-700">Recently resolved</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">For officials</div>
          <ul className="space-y-1.5 text-slate-600">
            <li><Link to={civicPath("/authorities")} className="hover:text-emerald-700">Authority sign-up</Link></li>
            <li><Link to={civicPath("/authorities")} className="hover:text-emerald-700">Dashboard preview</Link></li>
            <li><a href="https://pgportal.gov.in" target="_blank" rel="noreferrer" className="hover:text-emerald-700">CPGRAMS (Govt of India)</a></li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">Important</div>
          <p className="text-slate-500 text-xs leading-relaxed">
            CivicPulse is <b>not</b> an emergency service and <b>not</b> a
            government agency. In an emergency in India, dial <b>112</b>.
            We help route grievances but final resolution rests with the
            responsible authority.
          </p>
        </div>
      </div>
      <div className="border-t border-emerald-100 py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} CivicPulse.blog — Report • Connect • Resolve • Improve
      </div>
    </footer>
  );
}
